// CadenceEditor — US-128 (campaign daily dispatch limit + slider).
//
// Sub-uses DS-D `CadencePreview` for the inline timeline preview the user
// sees while they tune the limit (US-128 / US-136 share the visualisation).

import { useT } from '@barel/i18n';
import { Card, Slider } from '@barel/ui';

export interface CadenceEditorProps {
  /** Currently configured daily limit. */
  dailyLimit: number;
  onDailyLimitChange: (next: number) => void;
}

export function CadenceEditor({ dailyLimit, onDailyLimitChange }: CadenceEditorProps) {
  const t = useT('campaigns');

  return (
    <div className="flex flex-col gap-4" data-testid="cadence-editor">
      {/* Truthful guidance card. Real account-health recommendations require a
          server-owned signal and are deliberately not inferred in the browser. */}
      <Card padding="md" radius="2xl">
        <div className="flex items-center gap-4">
          <div
            aria-hidden="true"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-dim text-lg"
            style={{ border: '1px solid var(--accent-border)' }}
          >
            🛡️
          </div>
          <div className="flex-1 min-w-0">
            <strong className="text-primary text-sm">
              {t('create.sectionCadence.guidanceTitle')}
            </strong>
            <p className="text-tertiary text-xs leading-snug">
              {t('create.sectionCadence.guidanceBody')}
            </p>
          </div>
        </div>
      </Card>

      {/* Slider */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-secondary text-xs font-semibold">
            {t('create.sectionCadence.limitLabel')}
          </label>
          <span className="text-primary text-sm font-bold" data-testid="cadence-daily-value">
            {t('create.sectionCadence.limitValue', { value: dailyLimit })}
          </span>
        </div>
        <Slider
          value={[dailyLimit]}
          onValueChange={(values) => onDailyLimitChange(values[0] ?? dailyLimit)}
          min={5}
          max={40}
          step={1}
          aria-label={t('create.sectionCadence.limitLabel') as string}
        />
        <div className="flex justify-between text-tertiary text-xs mt-1">
          <span>{t('create.sectionCadence.limitMin')}</span>
          <span>{t('create.sectionCadence.limitMid')}</span>
          <span>{t('create.sectionCadence.limitMax')}</span>
        </div>
      </div>
    </div>
  );
}
